import React from 'react';
import { Skeleton } from '@heroui/react';
import useBreakpoint from './hooks/useBreakpoint.js';

/**
 * BootstrapLoading — HeroUI Skeleton placeholder shown while a calendar page
 * fetches its bootstrap context (organization + user + permissions + reference
 * data) from the Symfony APIs.
 *
 * WHY THIS TAKES A `variant`
 * This component started life in `calendar-month/` and drew a 6×7 month grid.
 * Day and Week imported it across from Month's folder, so BOTH flashed a month
 * calendar before snapping to a timeline — reported as "an imprint that
 * resembles the month view" on the Day View. That is worse than a plain
 * spinner: a skeleton's whole job is to preview the layout that is coming, and
 * this one promised the wrong shape and then contradicted itself.
 *
 * It is not a flicker either. Measured on dev: the bootstrap fan-out starts at
 * ~2.2s (right after DOMContentLoaded) and its reference data lands between
 * ~2.8s and ~4.0s, so the skeleton holds the screen for roughly 1.5–1.8s —
 * long enough to read.
 *
 * The nav row and filter slab are identical across all three views, so only
 * the grid body branches. Lives in `calendar-common/` because all three views
 * use it; reaching into `calendar-month/` is how the mismatch went unnoticed.
 *
 * @param {Object} props
 * @param {'month'|'week'|'day'} [props.variant='month'] Which grid rhythm to
 *   mimic. Defaults to `month` so the original call sites are unchanged.
 */
function BootstrapLoading({ variant = 'month' }) {
	// Match the live cell density: comfortable 128px (`h-32`) at desktop,
	// reduced 112px (`h-28`) on tablet/below-desktop — so the skeleton doesn't
	// imply a desktop grid when the real grid renders denser (DayCell parity).
	const { navCompact } = useBreakpoint();
	const cellHeight = navCompact ? 'h-28' : 'h-32';

	return (
		<div className="flex flex-col gap-4" data-testid={`${variant}-view-bootstrap-loading`}>
			{/* Nav row skeleton — shared by every view. */}
			<div className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-1.5">
					<Skeleton className="h-9 w-16 rounded-md" />
					<Skeleton className="h-9 w-9 rounded-md" />
					<Skeleton className="h-9 w-9 rounded-md" />
					<Skeleton className="h-9 w-40 rounded-md ml-2" />
				</div>
				<div className="flex items-center gap-1">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-8 w-20 rounded-md" />
					))}
				</div>
			</div>

			{/* Filter row placeholder — shared by every view. */}
			<Skeleton className="h-10 w-full rounded-md" />

			{variant === 'day' ? (
				/* Day: a sticky hour ruler, then resource lanes — each a 172px
				   label rail (RAIL_PX in DayTimelineGrid) beside a wide lane. */
				<div className="flex flex-col gap-1">
					<Skeleton className="h-[34px] w-full rounded-md" />
					{Array.from({ length: 8 }).map((_, i) => (
						<div key={i} className="flex gap-1">
							<Skeleton className="h-[58px] w-[172px] flex-none rounded-md" />
							<Skeleton className="h-[58px] flex-1 rounded-md" />
						</div>
					))}
				</div>
			) : variant === 'week' ? (
				/* Week: a narrow time gutter beside seven day columns. */
				<div className="flex gap-1">
					<Skeleton className="h-[520px] w-[56px] flex-none rounded-md" />
					<div className="grid flex-1 grid-cols-7 gap-1">
						{Array.from({ length: 7 }).map((_, i) => (
							<Skeleton key={i} className="h-[520px] rounded-md" />
						))}
					</div>
				</div>
			) : (
				/* Month: 6 rows × 7 cells, density matching the live grid. */
				<div className="grid grid-cols-7 gap-1">
					{Array.from({ length: 42 }).map((_, i) => (
						<Skeleton key={i} className={`${cellHeight} rounded-cal-cell`} />
					))}
				</div>
			)}
		</div>
	);
}

export default BootstrapLoading;
